import type { components } from '../../api/tipos'
import type { LineaLista } from './lista'

export type ResultadoResolucion = components['schemas']['ResultadoResolucion']
export type FichaOdoo = components['schemas']['FichaOdoo']
export type LineaPedido = components['schemas']['LineaPedido']
export type ClienteAPublicarEntrada = components['schemas']['ClienteAPublicarEntrada']

/** Una dirección de entrega elegida para multi-reparto (del hook useDireccionesEntrega). */
export interface DireccionEntregaElegida {
  id: number
  referencia?: string | null
  destinatario?: string | null
  direccion: string
  codigoPostal?: string | null
  ciudad?: string | null
  provincia?: string | null
}

/** La entrada de publicación puede llevar la dirección de entrega elegida (multi-reparto). */
export type ClienteAPublicarEntradaConDireccion = ClienteAPublicarEntrada & {
  direccion_entrega?: {
    id: number
    referencia?: string | null
    destinatario?: string | null
    direccion: string
    codigo_postal?: string | null
    ciudad?: string | null
    provincia?: string | null
  } | null
}

/**
 * El modelo que comparten los dos modos.
 *
 * El individual es un masivo de una fila: el mismo `POST /resolver` compone las
 * lineas, la misma regla decide si se puede publicar y el mismo `POST /publicar`
 * las crea. Tener dos caminos distintos habria significado dos sitios donde
 * olvidarse de comprobar que la ficha esta elegida, y esa comprobacion es la que
 * evita mandarle el pedido de un cliente a otro.
 *
 * Sobre la respuesta del backend se superponen tres decisiones del
 * administrador, que viven solo en la pantalla:
 *
 * - `fichas`: que ficha de Odoo es la buena cuando el NIF esta compartido.
 * - `precios`: el precio de una linea, cuando el del catalogo esta desfasado.
 * - `anadidas`: los productos que se le suman al pedido de ese cliente.
 *
 * Las tres se aplican en `lineasDe` y en `aEntradaPublicar`, que son los dos
 * unicos sitios desde los que se llega a Odoo. Una decision que no pase por
 * ahi se veria en la pantalla y no se publicaria.
 */

export interface FilaAgendamiento {
  /** Identificador estable de la fila dentro de la pantalla. */
  clave: string
  /** Numero de linea del texto pegado. Solo en el modo masivo. */
  numero?: number
  /** El texto original de esa linea, para poder ensenar lo que no se entendio. */
  texto?: string
  nif: string
  /** Lo que fallo al leer la linea, antes de preguntarle nada al backend. */
  errorLocal?: string
  resultado?: ResultadoResolucion
}

/** Ficha elegida a mano, por clave de fila. */
export type FichasElegidas = Record<string, number>

/** Precio editado a mano, indexado por `clave|indiceDeLinea`. */
export type PreciosEditados = Record<string, number>

/** Cantidad editada a mano, indexada por `clave|indiceDeLinea`. */
export type CantidadesEditadas = Record<string, number>

/**
 * Lineas quitadas a mano en la revision, por clave de fila: lista de indices
 * (sobre el orden de `lineasDe`) que el administrador ha decidido NO publicar.
 *
 * Opcion A, libertad total: cualquier linea se puede quitar, tambien las que
 * compuso el backend (bono, envases, devolucion). Es recuperable (se puede
 * restaurar), por eso se marca el indice en vez de borrar el dato: asi los
 * indices de las demas lineas no se mueven y las ediciones siguen cuadrando.
 */
export type LineasEliminadas = Record<string, number[]>

/**
 * El bono elegido a mano para una linea, indexado por `clave|indiceDeLinea`.
 *
 * Opcion A (editar en la propia linea): cambiar el bono sustituye el producto de
 * esa linea (nombre + product_id + precio), sin tocar las lineas de envase y
 * devolucion. Es libertad total: el administrador cuadra el pedido a ojo.
 */
export type BonosElegidos = Record<
  string,
  { nombre: string; product_id?: number | null; precio: number }
>

/**
 * Productos anadidos a mano al pedido de una fila, por clave de fila.
 *
 * Van **detras** de las lineas que compuso el backend, y ese orden importa:
 * los precios editados se indexan por posicion, asi que anadir al final deja
 * intactas las ediciones que ya hubiera.
 */
export type LineasAnadidas = Record<string, LineaPedido[]>

export function clavePrecio(clave: string, indice: number): string {
  return `${clave}|${indice}`
}

/**
 * La ficha de Odoo de esta fila, o `null` si no hay ninguna decidida.
 *
 * Lo que elige el administrador manda sobre lo que trajo el backend: si mira
 * las cuatro dependencias del Ayuntamiento y senala una, es que sabe cual es.
 */
export function partnerElegido(fila: FilaAgendamiento, fichas: FichasElegidas): number | null {
  const elegida = fichas[fila.clave]
  if (elegida !== undefined) return elegida
  return fila.resultado?.ficha?.id ?? null
}

function fichaDe(fila: FilaAgendamiento, fichas: FichasElegidas): FichaOdoo | null {
  const id = partnerElegido(fila, fichas)
  if (id === null) return null
  if (fila.resultado?.ficha?.id === id) return fila.resultado.ficha
  return fila.resultado?.candidatos?.find((candidata) => candidata.id === id) ?? null
}

/** Cuantas lineas trajo el backend. Las de despues son anadidas a mano. */
export function lineasDelBackend(fila: FilaAgendamiento): number {
  return (fila.resultado?.pedido?.lineas ?? []).length
}

/**
 * Las lineas que se publicarian: las que compuso el backend mas las anadidas,
 * todas con los precios que haya tocado el administrador.
 */
export function lineasDe(
  fila: FilaAgendamiento,
  precios: PreciosEditados,
  anadidas: LineasAnadidas = {},
  cantidades: CantidadesEditadas = {},
  bonos: BonosElegidos = {},
): LineaPedido[] {
  const lineas = [
    ...(fila.resultado?.pedido?.lineas ?? []),
    ...(anadidas[fila.clave] ?? []),
  ]
  return lineas.map((linea, indice) => {
    const clave = clavePrecio(fila.clave, indice)
    // 1) Si se eligio otro bono en la linea, sustituye producto + precio base.
    const bono = bonos[clave]
    let resultado: LineaPedido = bono
      ? { ...linea, nombre: bono.nombre, product_id: bono.product_id ?? linea.product_id, precio: bono.precio }
      : linea
    // 2) Precio editado a mano manda sobre el del catalogo / bono.
    const precioEditado = precios[clave]
    if (precioEditado !== undefined) resultado = { ...resultado, precio: precioEditado }
    // 3) Cantidad editada a mano.
    const cantidadEditada = cantidades[clave]
    if (cantidadEditada !== undefined) resultado = { ...resultado, cantidad: cantidadEditada }
    return resultado
  })
}

/**
 * El resumen que se lee en el evento del calendario y en la ruta.
 *
 * El del backend se respeta tal cual: dice "4 x BONO 4 18.9L", con las cuatro
 * garrafas, y eso no se puede recomponer desde las lineas (la del bono lleva
 * cantidad 1). Lo anadido se le concatena detras, en el mismo formato.
 *
 * Las cantidades negativas se quedan fuera, como en el backend: la devolucion
 * de envases es contabilidad, no algo que el repartidor tenga que cargar.
 */
export function resumenPedido(fila: FilaAgendamiento, anadidas: LineasAnadidas = {}): string {
  const base = fila.resultado?.pedido?.pedido_txt ?? ''
  const extra = (anadidas[fila.clave] ?? [])
    .filter((linea) => linea.cantidad > 0)
    .map((linea) => `${linea.cantidad} x ${linea.nombre}`)
  return [base, ...extra].filter((trozo) => trozo !== '').join(' + ')
}

/**
 * Por que esta fila no se puede publicar, o `null` si se puede.
 *
 * Devuelve el motivo y no un booleano a proposito: la tabla tiene que decir
 * **que** hay que arreglar. "No se puede publicar" sin mas obligaria al
 * administrador a adivinar cual de las veinte filas le falta algo.
 */
export function motivoNoPublicable(
  fila: FilaAgendamiento,
  fichas: FichasElegidas,
  anadidas: LineasAnadidas = {},
): string | null {
  if (fila.errorLocal) return fila.errorLocal

  const resultado = fila.resultado
  if (!resultado) return 'Esta linea todavia no se ha resuelto.'
  if (!resultado.cliente) {
    return `El NIF ${fila.nif} no esta en la lista de clientes: el agendamiento no da de alta clientes nuevos.`
  }
  if (partnerElegido(fila, fichas) === null) {
    return 'Falta elegir la ficha de Odoo de este cliente. Sin ella el pedido podria salir a otra direccion.'
  }
  // El error del backend deja de importar en cuanto hay lineas anadidas a
  // mano: "no se ha podido deducir su pedido habitual" es justo lo que el
  // administrador acaba de resolver eligiendo los productos.
  if (lineasDe(fila, {}, anadidas).length === 0) {
    return resultado.error ?? 'Esta fila no tiene ninguna linea de pedido.'
  }
  if (resultado.error && (anadidas[fila.clave] ?? []).length === 0) return resultado.error
  return null
}

export function aEntradaPublicar(
  fila: FilaAgendamiento,
  fichas: FichasElegidas,
  precios: PreciosEditados,
  anadidas: LineasAnadidas = {},
  fecha?: string,
  cantidades: CantidadesEditadas = {},
  bonos: BonosElegidos = {},
  eliminadas: LineasEliminadas = {},
): ClienteAPublicarEntrada | null {
  if (motivoNoPublicable(fila, fichas, anadidas) !== null) return null

  const cliente = fila.resultado?.cliente
  const ficha = fichaDe(fila, fichas)
  if (!cliente) return null

  // Opcion A: las lineas que el administrador ha quitado a mano no se publican.
  const quitadas = new Set(eliminadas[fila.clave] ?? [])
  const lineas = lineasDe(fila, precios, anadidas, cantidades, bonos).filter(
    (_, indice) => !quitadas.has(indice),
  )
  // Si se han quitado TODAS las lineas, no hay pedido que publicar.
  if (lineas.length === 0) return null

  return {
    contact_id: cliente.contact_id,
    nombre: cliente.nombre,
    code: cliente.code,
    partner_id: partnerElegido(fila, fichas),
    // La direccion es la de la **ficha de Odoo elegida**, no la de Postgres:
    // en un NIF compartido las dependencias tienen direcciones distintas, y el
    // evento del calendario tiene que llevar aquella a la que va el albaran.
    direccion: ficha?.calle || cliente.direccion || '',
    codigo_postal: ficha?.codigo_postal || cliente.codigo_postal || '',
    telefono: ficha?.telefono || cliente.telefono || '',
    lineas,
    pedido_txt: resumenPedido(fila, anadidas),
    nota: '',
    // Fecha por cliente (B.3): solo si difiere de la global; si no, se omite.
    ...(fecha ? { fecha } : {}),
  }
}

/**
 * Como `aEntradaPublicar`, pero expande el multi-reparto: si la fila tiene
 * direcciones de entrega elegidas, devuelve UNA entrada por dirección (cada una
 * con su `direccion_entrega`, misma facturación). Sin direcciones elegidas,
 * devuelve la entrada única de siempre. Devuelve [] si la fila no es publicable.
 */
export function aEntradasPublicar(
  fila: FilaAgendamiento,
  fichas: FichasElegidas,
  precios: PreciosEditados,
  anadidas: LineasAnadidas = {},
  fecha?: string,
  cantidades: CantidadesEditadas = {},
  bonos: BonosElegidos = {},
  eliminadas: LineasEliminadas = {},
  direccionesElegidas: DireccionEntregaElegida[] = [],
): ClienteAPublicarEntradaConDireccion[] {
  const base = aEntradaPublicar(fila, fichas, precios, anadidas, fecha, cantidades, bonos, eliminadas)
  if (!base) return []
  if (direccionesElegidas.length === 0) return [base]
  return direccionesElegidas.map((d) => {
    // id 0 = dirección PRINCIPAL del cliente: el albarán va a la ficha, sin
    // direccion_entrega (el backend usa la dirección de la ficha como siempre).
    if (d.id === 0) return base
    return {
      ...base,
      // El albarán va a la dirección elegida (misma facturación).
      direccion: d.direccion || base.direccion,
      codigo_postal: d.codigoPostal || base.codigo_postal,
      direccion_entrega: {
        id: d.id,
        referencia: d.referencia ?? null,
        destinatario: d.destinatario ?? null,
        direccion: d.direccion,
        codigo_postal: d.codigoPostal ?? null,
        ciudad: d.ciudad ?? null,
        provincia: d.provincia ?? null,
      },
    }
  })
}

/** Las filas que se publicarian ahora mismo. Las demas se quedan y se dice por que. */
export function publicables(
  filas: FilaAgendamiento[],
  fichas: FichasElegidas,
  anadidas: LineasAnadidas = {},
): FilaAgendamiento[] {
  return filas.filter((fila) => motivoNoPublicable(fila, fichas, anadidas) === null)
}

/**
 * Cose el analisis local con la respuesta del backend.
 *
 * `POST /resolver` solo recibe las lineas que se entendieron, y responde en ese
 * mismo orden. Aqui se vuelven a intercalar las que no se entendieron, en su
 * sitio: la revision tiene que ensenar la lista **entera**, o el administrador
 * publicaria diecinueve de veinte clientes sin enterarse del que falta.
 */
export function filasDeLista(
  lineas: LineaLista[],
  resultados: ResultadoResolucion[],
): FilaAgendamiento[] {
  let siguiente = 0
  return lineas.map((linea) => ({
    clave: `l${linea.numero}`,
    numero: linea.numero,
    texto: linea.texto,
    nif: linea.nif,
    errorLocal: linea.error,
    resultado: linea.error === undefined ? resultados[siguiente++] : undefined,
  }))
}
